import React from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { categories } from '../utils/constants';
import { getFullImageUrl } from '../utils/api';

const referenceStatusLabels = {
  ready: 'Visual reference ready',
  needs_review: 'Review visual reference',
  needs_replacement: 'Photo needed',
  description_only: 'Description only',
};

const DetectedItemsList = ({
  items,
  onEditItem,
  onRemoveItem,
  onBulkAdd,
  onReplaceReference,
  onRemoveReference,
  referenceUploadIndex,
  loading,
  showSourcePhoto,
}) => {
  return (
    <Box sx={{ mt: 3, p: 3, border: '2px solid', borderColor: 'success.main', borderRadius: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, color: 'success.main' }}>
        {items.length} Items Detected
      </Typography>
      <Typography variant="body2" sx={{ mb: 3 }}>
        Review and edit the detected items below, then add them all to your closet.
      </Typography>

      {items.map((item, index) => (
        <Box key={index} sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              Item {index + 1}
            </Typography>
            {showSourcePhoto && item.source_photo != null && (
              <Chip label={`Photo ${item.source_photo + 1}`} size="small" color="primary" variant="outlined" />
            )}
            <Chip
              label={`${Math.round((item.confidence?.overall ?? 0.5) * 100)}% confidence`}
              size="small"
              color={(item.confidence?.overall ?? 0.5) >= 0.7 ? 'success' : 'warning'}
              variant="outlined"
            />
            {(item.possible_duplicate_ids?.length > 0 || item.possible_duplicate_item_indexes?.length > 0) && (
              <Chip label="Possible duplicate" size="small" color="warning" />
            )}
            {showSourcePhoto && (
              <Chip
                label={referenceStatusLabels[item.visual_reference_status] || 'Description only'}
                size="small"
                color={item.visual_reference_status === 'ready' ? 'success' : 'warning'}
                variant={item.visual_reference_status === 'ready' ? 'filled' : 'outlined'}
              />
            )}
          </Box>

          {(item.confidence?.overall ?? 0.5) < 0.65 && (
            <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
              Low-confidence fields are highlighted for review. Confirm the category, color, and description before saving.
            </Typography>
          )}

          {showSourcePhoto && (
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'flex-start', sm: 'center' },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                mb: 2,
                p: 1.5,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
              }}
            >
              {item.image_url ? (
                <Box
                  component="img"
                  src={getFullImageUrl(item.image_url)}
                  alt={`Garment reference for item ${index + 1}`}
                  sx={{
                    width: 112,
                    height: 112,
                    objectFit: 'cover',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 112,
                    height: 112,
                    display: 'grid',
                    placeItems: 'center',
                    textAlign: 'center',
                    bgcolor: 'grey.100',
                    borderRadius: 2,
                    color: 'text.secondary',
                    p: 1,
                  }}
                >
                  <Typography variant="caption">No garment image</Typography>
                </Box>
              )}
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2">Garment image for future visualizations</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1 }}>
                  This crop helps the image model preserve the real garment's color, pattern, and shape.
                </Typography>
                {item.reference_warning && (
                  <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
                    {item.reference_warning}
                  </Typography>
                )}
                {onReplaceReference && (
                  <Button
                    component="label"
                    size="small"
                    variant="outlined"
                    disabled={referenceUploadIndex === index}
                    sx={{ mr: 1, mb: 0.5 }}
                  >
                    {referenceUploadIndex === index ? 'Uploading...' : 'Replace photo'}
                    <input
                      hidden
                      type="file"
                      accept="image/*"
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        if (file) onReplaceReference(index, file);
                        event.target.value = '';
                      }}
                    />
                  </Button>
                )}
                {item.image_url && onRemoveReference && (
                  <Button
                    size="small"
                    color="inherit"
                    onClick={() => onRemoveReference(index)}
                    sx={{ mb: 0.5 }}
                  >
                    Use description only
                  </Button>
                )}
              </Box>
            </Box>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Name"
                value={item.name || ''}
                onChange={(e) => onEditItem(index, 'name', e.target.value)}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={item.category || ''}
                  onChange={(e) => onEditItem(index, 'category', e.target.value)}
                  label="Category"
                >
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Color"
                value={item.color || ''}
                onChange={(e) => onEditItem(index, 'color', e.target.value)}
                size="small"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={item.description || ''}
                onChange={(e) => onEditItem(index, 'description', e.target.value)}
                multiline
                rows={2}
                size="small"
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                color="error"
                onClick={() => onRemoveItem(index)}
                size="small"
                sx={{ borderRadius: 2 }}
              >
                Remove Item
              </Button>
            </Grid>
          </Grid>
        </Box>
      ))}

      <Button
        variant="contained"
        color="success"
        onClick={onBulkAdd}
        disabled={loading || items.length === 0}
        fullWidth
        sx={{ mt: 2, py: 1.5, fontSize: '1rem', fontWeight: 'bold' }}
      >
        {loading ? 'Adding Items...' : `Add All ${items.length} Items to Closet`}
      </Button>
    </Box>
  );
};

export default DetectedItemsList;
